import { t } from '@trimble/i18n';
import { asError, getLogger } from '@trimble/logging';
import { Alert, Icon } from '@tcweb/connect-ui';
import { Key } from '@solid-primitives/keyed';
import {
  createVirtualizer,
  defaultRangeExtractor,
} from '@tanstack/solid-virtual';
import {
  createEffect,
  createMemo,
  createSignal,
  JSX,
  Match,
  on,
  onCleanup,
  Show,
  Switch,
  untrack,
} from 'solid-js';
import {
  ChildrenLoadStatus,
  FilterHandlerRecord,
  SelectionTreeVisibleContentRenderer,
  TreeNodeDefinition,
} from './types';
import {
  buildChildrenIndex,
  buildParentIdMap,
  filterNodes,
  filterNodesByText,
  flattenVisibleRows,
  getDescendantIds,
  toVisibleTreeRow,
  isFolder,
  shouldReserveVisibilityToggleSpace,
  shouldShowVisibilityToggle,
  sortNodes,
  type VisibleTreeRow,
} from './helpers';
import { Dynamic } from 'solid-js/web';
import { SortableKeyOf, SortDirection } from '../sort-drop-down-select';
import { ICONS, IconType } from '../tree/constants';
import TreeItem from '../tree/TreeItem';
import LoadableTreeAction from '../tree/LoadableTreeAction';
import Tree from '../tree/Tree';
import { SelectionTreeSlotContent } from './SelectionTreeTrailingContent';
import { createSelectionTreeChildLoads } from './selection-tree-child-loads';
import { createSelectionTreeExpansion } from './selection-tree-expansion';

const DISABLED_CONTENT_CLASS = 'text-base-muted';
const ERROR_CONTENT_CLASS = 'text-error';
const DEFAULT_VIRTUAL_OVERSCAN = 8;
const DEFAULT_ROW_HEIGHT = 36;
const STATUS_ROW_HEIGHT = 44;
const MAX_SPLICE_INSERT_ROWS = 10_000;
const LOADING_SKELETON_DELAY_MS = 80;
const TREE_INDENT_REM = 1.5;
const LOADING_NODE_ID_PREFIX = '__selection-tree-loading__';
const LOAD_ERROR_NODE_ID_PREFIX = '__selection-tree-load-error__';

function loadErrorNodeId(parentId: string): string {
  return `${LOAD_ERROR_NODE_ID_PREFIX}${parentId}`;
}

function isLoadErrorNodeId(nodeId: string): boolean {
  return nodeId.startsWith(LOAD_ERROR_NODE_ID_PREFIX);
}

function getActiveStickyIndex(
  stickyIndexes: readonly number[],
  startIndex: number
): number | undefined {
  for (let index = stickyIndexes.length - 1; index >= 0; index -= 1) {
    const stickyIndex = stickyIndexes[index];
    if (stickyIndex <= startIndex) return stickyIndex;
  }
  return undefined;
}

export interface SelectionTreeVirtualizationOptions {
  getScrollElement(): HTMLElement | null;
  /** Additional offset in the external scroll element's coordinate space. */
  scrollMargin?: number;
  overscan?: number;
  /** Keeps important controls mounted and pinned within the scrollport. */
  stickyNodeIds?: readonly string[];
  /** Supplies viewport dimensions in non-layout environments such as unit tests. */
  initialRect?: {
    readonly width: number;
    readonly height: number;
  };
}

function isNodeStatusDisabled(
  status: TreeNodeDefinition['status'] | undefined
): boolean {
  return (status?.disabled ?? false) || status?.status === 'failed';
}

function getStatusIcon(
  status: TreeNodeDefinition['status'] | undefined,
  isVisible: boolean,
  isRetrieved: boolean
): IconType {
  if (status?.status === 'error') {
    return ICONS.visibilityError;
  }

  if (isNodeStatusDisabled(status)) {
    return ICONS.file;
  }

  if (isVisible) {
    return ICONS.visibilityOn;
  }

  if (isRetrieved) {
    return ICONS.visibilityOff;
  }

  return ICONS.visibilityOffDownload;
}

function getStatusIconClass(
  status: TreeNodeDefinition['status'] | undefined,
  isVisible: boolean,
  visibilityToggleDisabled: boolean
): string {
  if (status?.status === 'error') {
    return ERROR_CONTENT_CLASS;
  }

  if (isNodeStatusDisabled(status)) {
    return DISABLED_CONTENT_CLASS;
  }

  if (visibilityToggleDisabled || !isVisible) {
    return 'opacity-60';
  }

  return '';
}

/**
 * Trailing chrome to the right of a row label.
 *
 * - `default`: browse and manage. Shows the node context menu and
 *   node-level `visibleContent`.
 * - `pick`: choose rows as references. Hides the context menu and
 *   node-level `visibleContent`. Tree-level `visibleContent` still renders.
 */
export type SelectionTreeRowMode = 'default' | 'pick';

export interface BaseSelectionTreeProps<T = unknown>
  extends ChildrenLoadStatus {
  errorLoadingText?: string;
  nodes: TreeNodeDefinition<T>[];
  sortBy?: SortableKeyOf<TreeNodeDefinition<T>>;
  sortDirection?: SortDirection;
  filterText?: string;
  highlightText?: string;
  filterMode?: string;
  filterHandlers?: FilterHandlerRecord<T>;
  defaultExpandIds?: string[];
  isNodeLoading(nodeId: string): boolean;
  isNodeVisible(nodeId: string): boolean;
  isNodeRetrieved?(nodeId: string): boolean;
  onVisibilityToggle(nodeId: string): void;
  onCancelVisibilityToggle?(nodeId: string): void;
  showCheckbox?: boolean;
  /** Whether selecting a selectable parent also selects its descendants. Defaults to true. */
  cascadeParentSelection?: boolean;
  onNodeExpand?(nodeId: string): void;
  onNodeCollapse?(nodeId: string): void;
  /**
   * Loads children on first expansion. Loaded rows are indexed and inserted
   * incrementally so large subtrees do not monopolize the main thread.
   */
  loadChildren?(nodeId: string): Promise<readonly TreeNodeDefinition<T>[]>;
  /**
   * Shows a placeholder immediately and swaps in the complete, sorted child
   * collection atomically. Intended for synthetic top-level nodes.
   */
  loadChildrenAtomically?(nodeId: string): boolean;
  /** When false, lazy-loaded rows are omitted from the tree index (host filter modes). */
  lazyChildrenEnabled?: boolean;
  virtualization?: SelectionTreeVirtualizationOptions;
  /**
   * Where the visibility toggle sits relative to the label icon.
   * Defaults to `afterLabelIcon`
   */
  visibilityTogglePosition?: 'beforeLabelIcon' | 'afterLabelIcon';
  /**
   * Optional render-time slot for row UI after the label and before the
   * context menu. When provided, its return value takes precedence over
   * `node.visibleContent` for that row.
   */
  visibleContent?: SelectionTreeVisibleContentRenderer<T>;
  /**
   * Controls trailing chrome to the right of the label. `pick` hides the
   * context menu and node-level `visibleContent`. Tree-level `visibleContent`
   * still renders. Omit or `default` keeps current behavior.
   */
  rowMode?: SelectionTreeRowMode;
}

export interface SingleSelectProps<T = unknown> {
  multiSelect: false;
  isNodeSelected?(nodeId: string): boolean;
  onNodeSelect?(
    nodeId: string,
    node: TreeNodeDefinition<T>,
    e: MouseEvent
  ): void;
}

export interface MultiSelectProps {
  multiSelect: true;
  isNodeSelected?(nodeId: string): boolean;
  onSelectionChange(selectedIds: string[]): void;
}

export type SelectionTreeProps<T = unknown> = BaseSelectionTreeProps<T> &
  (SingleSelectProps<T> | MultiSelectProps);

export const SelectionTree = <T,>(
  props: SelectionTreeProps<T>
): JSX.Element => {
  const expansion = createSelectionTreeExpansion(
    () =>
      `${props.filterMode ?? 'all'}|${(props.filterText ?? '').trim()}|${(props.highlightText ?? props.filterText ?? '').trim()}`
  );

  const defaultExpandIds = createMemo(() => props.defaultExpandIds);
  const lazyNodesById = new Map<string, TreeNodeDefinition<T>>();
  const loadingParentIds = new Set<string>();
  const loadingSkeletonTimers = new Map<string, number>();
  const [lazyRevision, setLazyRevision] = createSignal(0);
  const childLoads = createSelectionTreeChildLoads();
  const usesExternalResultSet = (): boolean => {
    const filter = (props.filterText ?? '').trim();
    const highlight = (props.highlightText ?? props.filterText ?? '').trim();
    return filter.length > 0 || highlight.length > 0;
  };
  const getIndexedNodes = (): TreeNodeDefinition<T>[] => {
    if (
      lazyNodesById.size === 0 ||
      usesExternalResultSet() ||
      props.lazyChildrenEnabled === false
    ) {
      return props.nodes;
    }
    const propNodeIds = new Set(props.nodes.map((node) => node.id));
    return [
      ...props.nodes,
      ...Array.from(lazyNodesById.values()).filter(
        (node) => !propNodeIds.has(node.id)
      ),
    ];
  };
  createEffect(
    on(defaultExpandIds, (ids, prev) => {
      // Add ids that appear later. Do not drop ids the user already expanded
      // or collapsed. A full replace would require remounting the tree.
      if (!ids) return;
      const previousIds = new Set(prev ?? []);
      const addedIds = ids.filter((id) => !previousIds.has(id));
      if (addedIds.length === 0) return;

      expansion.addDefaultExpandIds(addedIds);
      addedIds.forEach((id) => props.onNodeExpand?.(id));
    })
  );

  const parentIdMap = createMemo(() => {
    lazyRevision();
    return buildParentIdMap(getIndexedNodes());
  });
  const childrenIndex = createMemo(() => {
    lazyRevision();
    return buildChildrenIndex(getIndexedNodes());
  });
  const sortedChildrenIndex = createMemo(() => {
    const index = childrenIndex();
    const sortBy = props.sortBy;
    const sortDirection = props.sortDirection;
    if (sortBy === undefined || sortDirection === undefined) {
      return index;
    }

    return new Map(
      Array.from(index, ([parentId, children]) => [
        parentId,
        sortNodes(children, sortBy, sortDirection, index),
      ])
    );
  });

  const searchableNodes = createMemo(() => {
    const LAZY_SEARCH_THRESHOLD = 500;
    if (props.nodes.length <= LAZY_SEARCH_THRESHOLD) {
      return props.nodes;
    }

    const index = childrenIndex();
    const rootNodes = index.get(undefined) ?? [];
    const expanded = expansion.everExpandedIds();

    if (expanded.size === 0) return rootNodes;

    const expandedChildren = Array.from(expanded).flatMap(
      (id) => index.get(id) ?? []
    );
    return [...rootNodes, ...expandedChildren];
  });

  const normalizedFilter = createMemo(() =>
    (props.filterText ?? '').trim().toLowerCase()
  );
  const normalizedHighlight = createMemo(() =>
    (props.highlightText ?? props.filterText ?? '').trim().toLowerCase()
  );

  const filterResult = createMemo(() => {
    const isFilterModeActive =
      props.filterMode && props.filterMode !== 'all' && props.filterHandlers;
    const isTextFilterActive = normalizedFilter().length > 0;
    const isHighlightActive = normalizedHighlight().length > 0;

    let visibleIds: Set<string> | undefined;
    let expandIds = new Set<string>();
    let matchingIds = new Set<string>();
    let highlightText = '';

    if (isFilterModeActive) {
      const handler = props.filterHandlers![props.filterMode!];
      if (handler) {
        const result = filterNodes(
          getIndexedNodes(),
          handler.filter,
          parentIdMap()
        );
        visibleIds = result.visibleIds;
        expandIds = result.expandIds;
      }
    }

    if (isTextFilterActive) {
      const nodesToSearch = visibleIds
        ? searchableNodes().filter((n) => visibleIds!.has(n.id))
        : searchableNodes();

      const result = filterNodesByText(
        nodesToSearch,
        normalizedFilter(),
        parentIdMap()
      );

      visibleIds = result.visibleIds;
      matchingIds = result.matchingIds;
      highlightText = result.highlightText;
      expandIds = new Set([...expandIds, ...result.expandIds]);
    }

    if (
      isHighlightActive &&
      (!isTextFilterActive || normalizedHighlight() !== normalizedFilter())
    ) {
      const nodesToHighlight = visibleIds
        ? props.nodes.filter((n) => visibleIds!.has(n.id))
        : props.nodes;
      const result = filterNodesByText(
        nodesToHighlight,
        normalizedHighlight(),
        parentIdMap()
      );

      matchingIds = result.matchingIds;
      highlightText = result.highlightText;
    }

    return { visibleIds, expandIds, matchingIds, highlightText };
  });

  const isExpanded = (nodeId: string): boolean =>
    expansion.isExpanded(nodeId, filterResult().expandIds);

  const getSortedChildren = (
    parentId: string | undefined
  ): TreeNodeDefinition<T>[] => {
    const children = sortedChildrenIndex().get(parentId) ?? [];
    if (children.length === 0) return children;

    const { visibleIds } = filterResult();
    const filteredChildren = visibleIds
      ? children.filter((c) => visibleIds.has(c.id))
      : children;

    return filteredChildren;
  };

  const [selectionAnchorId, setSelectionAnchorId] = createSignal<
    string | undefined
  >(undefined);

  const isNodeSelectable = (node: TreeNodeDefinition<T>): boolean => {
    if (isLoadErrorNodeId(node.id)) return false;
    const folder = isFolder(node, childrenIndex());
    return (
      (!folder || node.allowSelectFolder === true) &&
      !isNodeStatusDisabled(node.status)
    );
  };

  const [visibleRows, setVisibleRows] = createSignal(
    [] as VisibleTreeRow<T>[],
    { equals: false }
  );
  let isExpandCollapseUpdate = false;
  let deferredStructureRebuild = false;

  const appendLoadErrorRows = (
    rows: VisibleTreeRow<T>[],
    failedIds: ReadonlySet<string>
  ): VisibleTreeRow<T>[] => {
    if (failedIds.size === 0) return rows;

    const next: VisibleTreeRow<T>[] = [];
    for (const row of rows) {
      next.push(row);
      if (!failedIds.has(row.id) || !isExpanded(row.id)) continue;
      next.push(
        toVisibleTreeRow(
          {
            id: loadErrorNodeId(row.id),
            parentId: row.id,
            label: '',
            isLeaf: true,
          },
          row.depth + 1
        )
      );
    }
    return next;
  };

  const rebuildVisibleRowsFromStructure = (): void => {
    if (isExpandCollapseUpdate) {
      deferredStructureRebuild = true;
      return;
    }
    const failedIds = childLoads.failedIds();
    setVisibleRows(
      untrack(() =>
        appendLoadErrorRows(
          flattenVisibleRows(getSortedChildren, isExpanded),
          failedIds
        )
      )
    );
  };

  createEffect(
    on(
      [
        () => props.nodes,
        () => props.sortBy,
        () => props.sortDirection,
        () => filterResult().visibleIds,
        () => props.lazyChildrenEnabled,
        lazyRevision,
        childLoads.failedIds,
      ],
      () => {
        rebuildVisibleRowsFromStructure();
      }
    )
  );

  const updateVisibleRowsForToggle = (
    nodeId: string,
    expanded: boolean,
    indexHint?: number
  ): void => {
    setVisibleRows((currentRows) => {
      const hintedRow =
        indexHint === undefined ? undefined : currentRows[indexHint];
      const nodeIndex: number =
        indexHint !== undefined && hintedRow?.id === nodeId
          ? indexHint
          : currentRows.findIndex((row) => row.id === nodeId);
      if (nodeIndex < 0) return currentRows;

      const nodeDepth = currentRows[nodeIndex].depth;
      if (expanded) {
        const insertedRows = flattenVisibleRows(
          getSortedChildren,
          isExpanded,
          nodeId,
          nodeDepth + 1
        );
        if (insertedRows.length === 0) return currentRows;
        if (insertedRows.length <= MAX_SPLICE_INSERT_ROWS) {
          currentRows.splice(nodeIndex + 1, 0, ...insertedRows);
          return currentRows;
        }
        return [
          ...currentRows.slice(0, nodeIndex + 1),
          ...insertedRows,
          ...currentRows.slice(nodeIndex + 1),
        ];
      }

      let endIndex = nodeIndex + 1;
      while (
        endIndex < currentRows.length &&
        currentRows[endIndex].depth > nodeDepth
      ) {
        endIndex += 1;
      }
      if (endIndex === nodeIndex + 1) return currentRows;
      currentRows.splice(nodeIndex + 1, endIndex - nodeIndex - 1);
      return currentRows;
    });
  };

  const loadingNodeId = (parentId: string): string =>
    `${LOADING_NODE_ID_PREFIX}${parentId}`;

  const showLoadingRow = (parentId: string, indexHint?: number): void => {
    setVisibleRows((currentRows) => {
      if (currentRows.some((row) => row.id === loadingNodeId(parentId))) {
        return currentRows;
      }
      const hintedRow =
        indexHint === undefined ? undefined : currentRows[indexHint];
      const parentIndex =
        hintedRow?.id === parentId
          ? indexHint
          : currentRows.findIndex((row) => row.id === parentId);
      if (parentIndex === undefined || parentIndex < 0) return currentRows;

      const parentDepth = currentRows[parentIndex].depth;
      const loadingId = loadingNodeId(parentId);
      const loadingNode = {
        id: loadingId,
        parentId,
        label: '',
        isLeaf: true,
        skeleton: true,
      };
      currentRows.splice(
        parentIndex + 1,
        0,
        toVisibleTreeRow(loadingNode, parentDepth + 1)
      );
      return currentRows;
    });
  };

  const indexLazyChildren = (
    parentId: string,
    nodes: readonly TreeNodeDefinition<T>[]
  ): TreeNodeDefinition<T>[] => {
    const parents = parentIdMap();
    const children = childrenIndex();
    const indexedChildren = [...(children.get(parentId) ?? [])];
    const addedNodeIds = new Set<string>();

    for (const node of nodes) {
      const alreadyIndexed = lazyNodesById.has(node.id);
      lazyNodesById.set(node.id, node);
      parents.set(node.id, node.parentId);
      if (alreadyIndexed) {
        continue;
      }
      addedNodeIds.add(node.id);
      indexedChildren.push(node);
    }

    children.set(parentId, indexedChildren);
    const sorted =
      props.sortBy === undefined || props.sortDirection === undefined
        ? indexedChildren
        : sortNodes(
            indexedChildren,
            props.sortBy,
            props.sortDirection,
            children
          );
    sortedChildrenIndex().set(parentId, sorted);
    setLazyRevision((current) => current + 1);
    return sorted.filter((node) => addedNodeIds.has(node.id));
  };

  const loadExpandedChildren = (
    nodeId: string,
    visibleIndex?: number
  ): void => {
    const loadAtomically = props.loadChildrenAtomically?.(nodeId) ?? false;
    if (loadingParentIds.has(nodeId)) {
      if (loadAtomically && getSortedChildren(nodeId).length === 0) {
        showLoadingRow(nodeId, visibleIndex);
      }
      return;
    }
    if (
      props.loadChildren === undefined ||
      getSortedChildren(nodeId).length > 0
    ) {
      return;
    }

    childLoads.transition(nodeId, 'start');
    loadingParentIds.add(nodeId);
    if (loadAtomically) {
      showLoadingRow(nodeId, visibleIndex);
    } else {
      loadingSkeletonTimers.set(
        nodeId,
        window.setTimeout(() => {
          untrack(() => {
            loadingSkeletonTimers.delete(nodeId);
            if (
              loadingParentIds.has(nodeId) &&
              isExpanded(nodeId) &&
              getSortedChildren(nodeId).length === 0
            ) {
              showLoadingRow(nodeId, visibleIndex);
            }
          });
        }, LOADING_SKELETON_DELAY_MS)
      );
    }
    const appendChildren = (nodes: readonly TreeNodeDefinition<T>[]): void => {
      if (nodes.length === 0) return;
      const timer = loadingSkeletonTimers.get(nodeId);
      if (timer !== undefined) window.clearTimeout(timer);
      loadingSkeletonTimers.delete(nodeId);
      untrack(() => {
        indexLazyChildren(nodeId, nodes);
        if (loadAtomically) {
          rebuildVisibleRowsFromStructure();
        }
      });
    };
    let loadFailed = false;
    void props
      .loadChildren(nodeId)
      .then((nodes) => untrack(() => appendChildren(nodes)))
      .catch((error: unknown) => {
        loadFailed = true;
        getLogger().error(
          'Failed to load selection tree children',
          { nodeId },
          asError(error)
        );
        // The same set does not notify, so the structure effect does not run.
        const alreadyFailed = childLoads.failedIds().has(nodeId);
        childLoads.transition(nodeId, 'fail');
        if (alreadyFailed) {
          rebuildVisibleRowsFromStructure();
        }
      })
      .finally(() => {
        const timer = loadingSkeletonTimers.get(nodeId);
        if (timer !== undefined) window.clearTimeout(timer);
        loadingSkeletonTimers.delete(nodeId);
        loadingParentIds.delete(nodeId);
        if (loadFailed) return;
        setVisibleRows((currentRows) => {
          const loadingIndex = currentRows.findIndex(
            (row) => row.id === loadingNodeId(nodeId)
          );
          if (loadingIndex >= 0) currentRows.splice(loadingIndex, 1);
          return currentRows;
        });
      });
  };

  const retryLoadChildren = (nodeId: string, visibleIndex?: number): void => {
    childLoads.transition(nodeId, 'start');
    loadExpandedChildren(nodeId, visibleIndex);
  };

  const pruneLazySubtree = (nodeId: string): void => {
    if (props.loadChildren === undefined) return;

    const index = childrenIndex();
    const removeIds: string[] = [];
    const collect = (parentId: string): void => {
      for (const child of index.get(parentId) ?? []) {
        removeIds.push(child.id);
        if (!child.isLeaf) {
          collect(child.id);
        }
      }
    };
    collect(nodeId);

    let removed = false;
    for (const id of removeIds) {
      if (lazyNodesById.delete(id)) {
        removed = true;
      }
    }
    if (removed) {
      expansion.forgetIds(removeIds);
      setLazyRevision((current) => current + 1);
    }
  };

  const handleExpandToggle = (
    nodeId: string,
    nextExpanded: boolean,
    visibleIndex?: number
  ): void => {
    isExpandCollapseUpdate = true;
    expansion.toggleExpanded(nodeId, nextExpanded);
    updateVisibleRowsForToggle(nodeId, nextExpanded, visibleIndex);
    if (nextExpanded) {
      loadExpandedChildren(nodeId, visibleIndex);
      props.onNodeExpand?.(nodeId);
    } else {
      pruneLazySubtree(nodeId);
      props.onNodeCollapse?.(nodeId);
    }
    queueMicrotask(() => {
      isExpandCollapseUpdate = false;
      if (deferredStructureRebuild) {
        deferredStructureRebuild = false;
        rebuildVisibleRowsFromStructure();
      }
    });
  };

  onCleanup(() => {
    loadingSkeletonTimers.forEach((timer) => window.clearTimeout(timer));
    loadingSkeletonTimers.clear();
  });

  // Selectable rows in the exact order they are rendered.
  const orderedSelectableIds = (): string[] =>
    visibleRows()
      .filter((row) => isNodeSelectable(row.renderNode))
      .map((row) => row.id);

  const currentSelectedIds = (): Set<string> => {
    const selected = new Set<string>();
    for (const node of getIndexedNodes()) {
      if (isNodeSelectable(node) && props.isNodeSelected?.(node.id)) {
        selected.add(node.id);
      }
    }
    return selected;
  };

  const addWithDescendants = (ids: Set<string>, nodeId: string): void => {
    ids.add(nodeId);
    if (props.cascadeParentSelection === false) return;
    getDescendantIds(nodeId, childrenIndex(), isNodeSelectable).forEach((id) =>
      ids.add(id)
    );
  };

  const removeWithDescendants = (ids: Set<string>, nodeId: string): void => {
    ids.delete(nodeId);
    if (props.cascadeParentSelection === false) return;
    getDescendantIds(nodeId, childrenIndex(), isNodeSelectable).forEach((id) =>
      ids.delete(id)
    );
  };

  const handleMultiSelect = (nodeId: string, e: MouseEvent): void => {
    const ordered = orderedSelectableIds();
    const targetIndex = ordered.indexOf(nodeId);
    const selected = currentSelectedIds();
    const anchor = selected.size === 0 ? undefined : selectionAnchorId();
    const hasModifier = e.ctrlKey || e.metaKey;
    const isSelected = props.isNodeSelected?.(nodeId) ?? false;
    let next: Set<string>;

    if (targetIndex === -1) {
      next = selected;
    } else if (e.shiftKey && anchor && ordered.includes(anchor)) {
      const start = ordered.indexOf(anchor);
      const [lo, hi] =
        start <= targetIndex ? [start, targetIndex] : [targetIndex, start];
      const range = ordered.slice(lo, hi + 1);
      next = hasModifier ? new Set(selected) : new Set<string>();
      range.forEach((id) => addWithDescendants(next, id));
      // The anchor is preserved across shift-clicks so the range can be resized.
    } else if (hasModifier) {
      next = new Set(selected);
      if (next.has(nodeId)) removeWithDescendants(next, nodeId);
      else addWithDescendants(next, nodeId);
      setSelectionAnchorId(nodeId);
    } else if (isSelected) {
      next = new Set(selected);
      removeWithDescendants(next, nodeId);
      setSelectionAnchorId(undefined);
    } else {
      next = new Set<string>();
      addWithDescendants(next, nodeId);
      setSelectionAnchorId(nodeId);
    }

    if (props.multiSelect) {
      props.onSelectionChange?.(Array.from(next));
    }
  };

  const [virtualHost, setVirtualHost] = createSignal<HTMLDivElement>();
  const [measuredScrollMargin, setMeasuredScrollMargin] = createSignal(0);
  const [elevatedVirtualRowIndex, setElevatedVirtualRowIndex] = createSignal<
    number | undefined
  >(undefined);
  const stickyRowIndexes = createMemo(() => {
    const stickyNodeIds = new Set(props.virtualization?.stickyNodeIds ?? []);
    if (stickyNodeIds.size === 0) return [];

    return visibleRows()
      .map((row, index) => (stickyNodeIds.has(row.id) ? index : -1))
      .filter((index) => index >= 0);
  });
  const getVirtualScrollElement = (): HTMLElement | null =>
    props.virtualization?.getScrollElement() ?? null;
  const useVirtualization = createMemo(
    () =>
      props.virtualization !== undefined && getVirtualScrollElement() !== null
  );

  const virtualizer = createVirtualizer<HTMLElement, HTMLDivElement>({
    get count() {
      return useVirtualization() ? visibleRows().length : 0;
    },
    getScrollElement: getVirtualScrollElement,
    estimateSize: (index) =>
      visibleRows()[index]?.renderNode.status?.message == null
        ? DEFAULT_ROW_HEIGHT
        : STATUS_ROW_HEIGHT,
    get overscan() {
      return props.virtualization?.overscan ?? DEFAULT_VIRTUAL_OVERSCAN;
    },
    get scrollMargin() {
      return measuredScrollMargin();
    },
    get initialRect() {
      return props.virtualization?.initialRect;
    },
    getItemKey: (index) => visibleRows()[index]?.id ?? index,
    measureElement: (element) => element.getBoundingClientRect().height,
    rangeExtractor: (range) => {
      const activeStickyIndex = getActiveStickyIndex(
        stickyRowIndexes(),
        range.startIndex
      );
      return Array.from(
        new Set([
          ...(activeStickyIndex === undefined ? [] : [activeStickyIndex]),
          ...defaultRangeExtractor(range),
        ])
      ).sort((left, right) => left - right);
    },
  });

  const estimatedVirtualContentHeight = createMemo(() => {
    void lazyRevision();
    return visibleRows().reduce((total, row) => {
      return (
        total +
        (row.renderNode.status?.message == null
          ? DEFAULT_ROW_HEIGHT
          : STATUS_ROW_HEIGHT)
      );
    }, 0);
  });

  const virtualContentHeight = createMemo(() => {
    if (!useVirtualization()) {
      return 0;
    }
    const measured = virtualizer.getTotalSize();
    const estimated = estimatedVirtualContentHeight();
    return Math.max(measured, estimated);
  });

  createEffect(() => {
    void visibleRows().length;
    void estimatedVirtualContentHeight();
    if (!useVirtualization()) {
      return;
    }
    queueMicrotask(() => {
      virtualizer.measure();
    });
  });

  createEffect(() => {
    const host = virtualHost();
    const scrollElement = getVirtualScrollElement();
    const additionalScrollMargin = props.virtualization?.scrollMargin ?? 0;
    if (!props.virtualization || !host || !scrollElement) {
      setMeasuredScrollMargin(additionalScrollMargin);
      return;
    }

    let animationFrame: number | undefined;
    const updateScrollMargin = (): void => {
      animationFrame = undefined;
      const hostRect = host.getBoundingClientRect();
      const scrollRect = scrollElement.getBoundingClientRect();
      const nextMargin =
        hostRect.top -
        scrollRect.top +
        scrollElement.scrollTop -
        scrollElement.clientTop +
        additionalScrollMargin;
      setMeasuredScrollMargin(Math.max(0, nextMargin));
      virtualizer.measure();
    };
    const scheduleUpdate = (): void => {
      if (animationFrame !== undefined) return;
      animationFrame = window.requestAnimationFrame(updateScrollMargin);
    };

    updateScrollMargin();

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(scheduleUpdate);
    let currentElement: HTMLElement | null = host;
    while (currentElement) {
      resizeObserver?.observe(currentElement);
      if (currentElement === scrollElement) break;
      currentElement = currentElement.parentElement;
    }
    for (const child of scrollElement.children) {
      if (child instanceof HTMLElement) {
        resizeObserver?.observe(child);
      }
    }

    window.addEventListener('resize', scheduleUpdate);

    createEffect(() => {
      if (isExpandCollapseUpdate) {
        return;
      }
      void visibleRows().length;
      void virtualizer.getTotalSize();
      scheduleUpdate();
    });

    onCleanup(() => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
      if (animationFrame !== undefined) {
        window.cancelAnimationFrame(animationFrame);
      }
    });
  });

  type RenderNodeProps = TreeNodeDefinition<T> & {
    readonly depth?: number;
    readonly renderChildren?: boolean;
    onContextMenuMount?(): void;
    onContextMenuUnmount?(): void;
    readonly visibleIndex?: number;
  };

  const InteractiveNode = (nodeProps: RenderNodeProps): JSX.Element => {
    const hasChildren = createMemo(() => isFolder(nodeProps, childrenIndex()));

    const expanded = createMemo(() => isExpanded(nodeProps.id));

    const children = createMemo(() => {
      lazyRevision();
      return nodeProps.renderChildren !== false && hasChildren() && expanded()
        ? getSortedChildren(nodeProps.id)
        : [];
    });

    const highlightText = createMemo(() => {
      const { matchingIds, highlightText } = filterResult();
      return matchingIds.has(nodeProps.id) ? highlightText : undefined;
    });

    const showVisibilityToggle = createMemo(() =>
      shouldShowVisibilityToggle(nodeProps, hasChildren())
    );

    const reserveVisibilityToggleSpace = createMemo(() => {
      const parentId = parentIdMap().get(nodeProps.id);
      const siblings = childrenIndex().get(parentId) ?? [];
      return shouldReserveVisibilityToggleSpace(
        nodeProps,
        hasChildren(),
        siblings,
        childrenIndex()
      );
    });

    const statusIndicator = createMemo<{
      icon: IconType;
      iconClass: string;
      labelColor?: string;
      messageColor?: string;
      message?: string;
      disabled: boolean;
      visibilityToggleDisabled: boolean;
    }>(() => {
      const status = nodeProps.status?.status;
      const disabled = isNodeStatusDisabled(nodeProps.status);
      const visibilityToggleDisabled =
        nodeProps.visibilityToggleDisabled ?? disabled;
      const message = nodeProps.status?.message;
      const isVisible = nodeProps.skeleton
        ? false
        : props.isNodeVisible(nodeProps.id);
      const isRetrieved = nodeProps.skeleton
        ? false
        : (props.isNodeRetrieved?.(nodeProps.id) ?? isVisible);
      const icon = getStatusIcon(nodeProps.status, isVisible, isRetrieved);
      const iconClass = getStatusIconClass(
        nodeProps.status,
        isVisible,
        visibilityToggleDisabled
      );

      const labelColor = status === 'error' ? ERROR_CONTENT_CLASS : undefined;

      const explicitMessageClass = nodeProps.status?.messageClass;
      const explicitMessageColor = nodeProps.status?.messageColor;
      const isErrorMessage =
        explicitMessageColor === 'error' ||
        (explicitMessageColor === undefined && status === 'error');
      const messageColor =
        explicitMessageClass ??
        (isErrorMessage ? ERROR_CONTENT_CLASS : DISABLED_CONTENT_CLASS);

      return {
        icon,
        iconClass,
        labelColor,
        message,
        messageColor,
        disabled,
        visibilityToggleDisabled,
      };
    });

    const togglesBeforeLabelIcon = (): boolean =>
      props.visibilityTogglePosition === 'beforeLabelIcon';

    // Own component per placement branch so each position builds its own nodes.
    const VisibilityToggle = (): JSX.Element => (
      <Show when={showVisibilityToggle() || reserveVisibilityToggleSpace()}>
        <div class="w-10 flex-shrink-0" aria-hidden={!showVisibilityToggle()}>
          <Show when={showVisibilityToggle()}>
            <LoadableTreeAction
              label={nodeProps.toggleTooltip ?? ''}
              icon={statusIndicator().icon}
              class={statusIndicator().iconClass}
              isLoading={props.isNodeLoading(nodeProps.id)}
              onCancelClick={() => {
                props.onCancelVisibilityToggle?.(nodeProps.id);
              }}
              disabled={statusIndicator().visibilityToggleDisabled}
              onClick={() => props.onVisibilityToggle(nodeProps.id)}
            />
          </Show>
        </div>
      </Show>
    );

    // eslint-disable-next-line @typescript-eslint/no-deprecated, sonarjs/deprecation -- labelIcon takes a Modus icon name, not an image URL, so it cannot replace labelSvgIcon
    const labelSvgIcon = (): string | undefined => nodeProps.labelSvgIcon;

    return (
      <div
        data-testid={
          nodeProps.dataTestId ??
          (hasChildren() ? 'selection-tree-node-parent' : 'selection-tree-node')
        }
        class="contents"
      >
        <TreeItem
          class={statusIndicator().disabled ? DISABLED_CONTENT_CLASS : ''}
          disabled={statusIndicator().disabled}
          hasChildren={hasChildren()}
          allowSelectFolder={nodeProps.allowSelectFolder}
          selected={props.isNodeSelected?.(nodeProps.id)}
          node={{
            label: nodeProps.label,
            isExpanded: expanded(),
            highlightText: highlightText(),
            statusMessage: statusIndicator().message,
            labelColor: statusIndicator().labelColor,
            statusMessageColor: statusIndicator().messageColor,
          }}
          prefixContent={() => (
            <>
              <Show when={togglesBeforeLabelIcon()}>
                <VisibilityToggle />
              </Show>
              <Show when={labelSvgIcon()}>
                <img
                  src={labelSvgIcon()}
                  alt=""
                  class="mr-2 flex min-h-5 min-w-5"
                />
              </Show>
              <Show
                when={
                  !labelSvgIcon() &&
                  !nodeProps.labelCssIcon &&
                  nodeProps.labelIcon
                }
              >
                {(icon) => (
                  <Icon icon={icon()} size="icon-medium" class="mr-2" />
                )}
              </Show>
              <Show when={!labelSvgIcon() && nodeProps.labelCssIcon}>
                {(iconClass) => (
                  <i
                    class={`${iconClass()} mr-2 flex-shrink-0`}
                    aria-hidden="true"
                  />
                )}
              </Show>
              <Show when={!togglesBeforeLabelIcon()}>
                <VisibilityToggle />
              </Show>
            </>
          )}
          labelSuffix={nodeProps.labelSuffix}
          visibleContent={
            props.visibleContent !== undefined ||
            (props.rowMode !== 'pick' && nodeProps.visibleContent !== undefined)
              ? () => (
                  <SelectionTreeSlotContent
                    node={nodeProps}
                    nodes={props.nodes}
                    treeSlot={props.visibleContent}
                    nodeSlot={
                      props.rowMode === 'pick'
                        ? undefined
                        : nodeProps.visibleContent
                    }
                  />
                )
              : undefined
          }
          onExpandToggle={(newExpanded) =>
            handleExpandToggle(
              nodeProps.id,
              newExpanded,
              nodeProps.visibleIndex
            )
          }
          onSelect={
            props.multiSelect || props.onNodeSelect
              ? (e: MouseEvent) => {
                  if (props.multiSelect) {
                    handleMultiSelect(nodeProps.id, e);
                  } else {
                    props.onNodeSelect?.(nodeProps.id, nodeProps, e);
                  }
                }
              : undefined
          }
          contextMenuOptions={
            props.rowMode === 'pick' ? undefined : nodeProps.contextMenuOptions
          }
          onContextMenuMount={nodeProps.onContextMenuMount}
          onContextMenuUnmount={nodeProps.onContextMenuUnmount}
          depth={nodeProps.depth}
          showCheckbox={props.showCheckbox && isNodeSelectable(nodeProps)}
        >
          {/* Keyed by node id: when node definitions are rebuilt (e.g. a run's
              load state changes), unchanged rows keep their DOM and tooltip
              instances instead of being disposed and recreated. */}
          <Key each={children()} by={(childNode) => childNode.id}>
            {(childNode) => <Node {...childNode()} />}
          </Key>
        </TreeItem>
        <Show
          when={
            nodeProps.renderChildren !== false &&
            expanded() &&
            childLoads.failedIds().has(nodeProps.id)
          }
        >
          <LoadErrorNode
            id={loadErrorNodeId(nodeProps.id)}
            parentId={nodeProps.id}
            label=""
            isLeaf={true}
            depth={(nodeProps.depth ?? 0) + 1}
            visibleIndex={nodeProps.visibleIndex}
          />
        </Show>
      </div>
    );
  };

  const SkeletonNode = (nodeProps: RenderNodeProps): JSX.Element => (
    <div
      data-testid="selection-tree-loading-skeleton"
      class="flex h-9 items-center animate-pulse"
      style={{
        'padding-left': `${String(
          (nodeProps.depth ?? 0) * TREE_INDENT_REM
        )}rem`,
      }}
      aria-label="Loading children"
    >
      <div class="ml-2 h-4 w-2/3 rounded-sm bg-trimble-gray-200" />
    </div>
  );

  const LoadErrorNode = (nodeProps: RenderNodeProps): JSX.Element => {
    return (
      <div
        data-testid="selection-tree-load-error"
        class="flex h-9 items-center gap-2 pr-2 text-sm text-error"
        style={{
          'padding-left': `${String((nodeProps.depth ?? 0) * TREE_INDENT_REM)}rem`,
        }}
        role="alert"
      >
        <span class="truncate">{t('general.unhandledError')}</span>
        <button
          type="button"
          class="shrink-0 cursor-pointer text-sm text-blue-600 underline"
          data-testid="selection-tree-load-retry"
          onClick={() => {
            const parentId = nodeProps.parentId;
            if (parentId === undefined) return;
            retryLoadChildren(parentId, nodeProps.visibleIndex);
          }}
        >
          {t('general.retry')}
        </button>
      </div>
    );
  };

  const Node = (nodeProps: RenderNodeProps): JSX.Element => (
    <Dynamic
      component={
        nodeProps.skeleton
          ? SkeletonNode
          : isLoadErrorNodeId(nodeProps.id)
            ? LoadErrorNode
            : InteractiveNode
      }
      {...nodeProps}
    />
  );

  const rootNodes = createMemo(() => getSortedChildren(undefined));

  const VIRTUAL_ROW_ELEVATED_Z = 'z-50';

  const getVirtualRowClass = (
    virtualIndex: number,
    isSticky: boolean
  ): string => {
    const elevated = elevatedVirtualRowIndex() === virtualIndex;
    if (isSticky) {
      return elevated
        ? `sticky left-0 top-0 ${VIRTUAL_ROW_ELEVATED_Z} w-full bg-base-page`
        : 'sticky left-0 top-0 z-10 w-full bg-base-page';
    }
    return elevated
      ? `absolute left-0 top-0 ${VIRTUAL_ROW_ELEVATED_Z} w-full`
      : 'absolute left-0 top-0 w-full';
  };

  return (
    <Switch>
      <Match when={props.childrenLoadStatus === 'error'}>
        <Alert type="error">
          {props.errorLoadingText ?? t('general.unhandledError')}
        </Alert>
      </Match>
      <Match when={props.childrenLoadStatus === 'success'}>
        <div ref={setVirtualHost} data-testid="selection-tree">
          <Show
            when={useVirtualization()}
            fallback={
              <Tree>
                <Key each={rootNodes()} by={(childNode) => childNode.id}>
                  {(childNodeProps) => <Node {...childNodeProps()} />}
                </Key>
              </Tree>
            }
          >
            <div
              data-testid="selection-tree-virtual-content"
              class="relative w-full"
              style={{ height: `${virtualContentHeight()}px` }}
            >
              <Key
                each={virtualizer.getVirtualItems()}
                by={(virtualItem) => virtualItem.key}
              >
                {(virtualItem) => {
                  const row = ():
                    | ReturnType<typeof visibleRows>[number]
                    | undefined => visibleRows()[virtualItem().index];
                  const isActiveSticky = (): boolean =>
                    getActiveStickyIndex(
                      stickyRowIndexes(),
                      virtualizer.range?.startIndex ?? 0
                    ) === virtualItem().index;
                  return (
                    <Show when={row()}>
                      {(visibleRow) => (
                        <div
                          data-index={virtualItem().index}
                          ref={virtualizer.measureElement}
                          class={getVirtualRowClass(
                            virtualItem().index,
                            isActiveSticky()
                          )}
                          style={{
                            height: `${virtualItem().size}px`,
                            transform: isActiveSticky()
                              ? undefined
                              : `translateY(${
                                  virtualItem().start - measuredScrollMargin()
                                }px)`,
                          }}
                        >
                          <Node
                            {...visibleRow().renderNode}
                            depth={visibleRow().depth}
                            renderChildren={false}
                            visibleIndex={virtualItem().index}
                            onContextMenuMount={() => {
                              setElevatedVirtualRowIndex(virtualItem().index);
                            }}
                            onContextMenuUnmount={() => {
                              setElevatedVirtualRowIndex((current) =>
                                current === virtualItem().index
                                  ? undefined
                                  : current
                              );
                            }}
                          />
                        </div>
                      )}
                    </Show>
                  );
                }}
              </Key>
            </div>
          </Show>
        </div>
      </Match>
    </Switch>
  );
};
